// src/components/Plan.js

import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../../shared/components/layout/Layout';
import PreferencesWizard from '../components/PreferencesWizard';
import MenuDisplay from '../components/MenuDisplay';
import SavedMenus from '../components/SavedMenus';
import WeekSection from '../components/WeekSection';
import { Alert, Button } from '../../../shared/components/ui';
import { menuService } from '../services/menuService';
import { shoppingListService } from '../../shopping-list/services/shoppingListService';
import { mealService } from '../../meal/services/mealService';
import { feedbackService } from '../../meal/services/feedbackService';
import { checkUserPreferences } from '../../../shared/utils/preferencesHelper';
import { useTranslation } from 'react-i18next';

const Plan = () => {

    const { t } = useTranslation();
    const [menu, setMenu] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [menuStatus, setMenuStatus] = useState(null);
    const [allMenus, setAllMenus] = useState([]);
    const [isLoadingMenus, setIsLoadingMenus] = useState(true);
    const [isConfirmingWeek, setIsConfirmingWeek] = useState(false);
    const [isWeekConfirmed, setIsWeekConfirmed] = useState(false);
    const [menuMetadata, setMenuMetadata] = useState(null);
    const [showPreferencesWizard, setShowPreferencesWizard] = useState(false);
    const [hasPreferences, setHasPreferences] = useState(false);

    // Estados para las 3 secciones
    const [currentWeekMenus, setCurrentWeekMenus] = useState([]);
    const [nextWeekMenus, setNextWeekMenus] = useState([]);
    const [previousWeeksMenus, setPreviousWeeksMenus] = useState({});
    const [currentWeekMetadata, setCurrentWeekMetadata] = useState(null);
    const [nextWeekMetadata, setNextWeekMetadata] = useState(null);

    // Estado para tabs - activar currentWeek por defecto
    const [activeTab, setActiveTab] = useState('currentWeek');

    // Referencia para evitar llamadas duplicadas en React StrictMode
    const hasLoadedData = useRef(false);

    // Verificar preferencias al montar el componente
    useEffect(() => {
        checkPreferences();
    }, []);

    // Cargar el estado de los menús al montar el componente
    useEffect(() => {
        // Solo cargar datos si ya tiene preferencias
        if (hasPreferences && !hasLoadedData.current) {
            hasLoadedData.current = true;
            loadInitialData();
        }
    }, [hasPreferences]);

    // Polling cada 30 segundos mientras no haya menús seteados
    useEffect(() => {
        // Solo hacer polling si tiene preferencias y no hay menús
        if (!hasPreferences || allMenus.length > 0) {
            return;
        }

        const intervalId = setInterval(() => {
            loadInitialData();
        }, 30000); // 30 segundos

        return () => clearInterval(intervalId);
    }, [hasPreferences, allMenus.length]);

    // Cambiar automáticamente a "próxima semana" si no hay menús en semana en curso
    useEffect(() => {
        if (activeTab === 'currentWeek' &&
            (!currentWeekMenus || currentWeekMenus.length === 0) &&
            nextWeekMenus &&
            nextWeekMenus.length > 0 &&
            !isLoadingMenus) {
            setActiveTab('nextWeek');
        }
    }, [currentWeekMenus, nextWeekMenus, activeTab, isLoadingMenus]);

    // Verificar si el usuario tiene preferencias configuradas
    const checkPreferences = async () => {
        try {
            const result = await checkUserPreferences();
            setHasPreferences(result.hasPreferences);
            setShowPreferencesWizard(!result.hasPreferences);
        } catch (error) {
            setHasPreferences(false);
            setShowPreferencesWizard(true);
        }
    };

    // Función consolidada para cargar todos los datos iniciales
    const loadInitialData = async () => {
        setIsLoadingMenus(true);
        try {
            // Cargar estado de menús y todos los menús en paralelo
            const [status, normalizedData] = await Promise.all([
                menuService.getMenuStatus(),
                menuService.getAllMenus()
            ]);

            setMenuStatus(status);

            // Usar estructura normalizada
            const { currentWeek, nextWeek, previousWeeks, legacy } = normalizedData;

            // Para retrocompatibilidad: mantener allMenus como próxima semana
            setAllMenus(nextWeek.menus || []);

            // Verificar estado de confirmación usando la metadata del menú de próxima semana
            const menuMetadata = nextWeek.metadata || legacy.metadata;
            setMenuMetadata(menuMetadata);
            if (menuMetadata && menuMetadata.status === 'reviewed') {
                // Verificar que haya meals en la semana
                const hasMeals = nextWeek.menus.some(menu => {
                    return ['breakfast', 'lunch', 'dinner'].some(mealType =>
                        menu[mealType] && menu[mealType].id
                    );
                });
                setIsWeekConfirmed(hasMeals);
            } else {
                setIsWeekConfirmed(false);
            }

            // Almacenar datos de las 3 secciones para renderizado
            // Estos se pueden usar más adelante cuando refactoricemos el renderizado
            setCurrentWeekMenus(currentWeek.menus || []);
            setNextWeekMenus(nextWeek.menus || []);
            setPreviousWeeksMenus(previousWeeks || {});
            setCurrentWeekMetadata(currentWeek.metadata);
            setNextWeekMetadata(nextWeek.metadata);
        } catch (err) {
        } finally {
            setIsLoadingMenus(false);
        }
    };



    // 5. Maneja la confirmación del menú y generación de lista de compra
    const handleMenuConfirmation = async () => {
        if (!menu) return;

        setIsLoading(true);
        setError('');
        try {
            const list = await shoppingListService.generateShoppingList(menu);
            // No establecer shoppingList aquí, solo generar la lista
        } catch (err) {
            setError(t('shoppingList.errors.loadGeneral'));
        } finally {
            setIsLoading(false);
        }
    };

    // 6. Maneja la selección de un menú guardado
    const handleMenuSelect = (selectedMenu) => {
        setMenu(selectedMenu);
    };



    // 8. Maneja el toggle de repetible para un meal
    const handleToggleRepeatable = async (mealId) => {
        try {
            await mealService.toggleMealRepeatable(mealId);
            // Recargar los menús para reflejar el cambio
            await loadInitialData();
        } catch (error) {
            throw error; // Re-throw para que MenuDisplay pueda manejarlo
        }
    };

    // 9. Confirmar semana y generar lista de compra
    const handleConfirmWeek = async () => {
        setIsConfirmingWeek(true);
        setError('');
        try {
            const result = await menuService.confirmWeekAndGenerateShoppingList();

            if (result.success) {
                // Recargar los datos para reflejar el estado actualizado
                await loadInitialData();
            } else {
                setError(result.error || t('plan.confirm.error') || t('common.error'));
            }
        } catch (err) {
            setError(t('plan.confirm.networkError') || t('common.error'));
        } finally {
            setIsConfirmingWeek(false);
        }
    };

    // 10. Maneja la finalización del wizard de preferencias
    const handlePreferencesCompleted = async (preferences) => {
        try {
            // Transformar los datos del frontend al formato del backend
            const backendPreferences = {
                num_people: preferences.numPeople,
                allergies: preferences.allergies || [],
                dietary_preferences: preferences.preferences || [],
                budget_level: preferences.budget,
                complexity: preferences.complexity || 'media',
                goal: preferences.goal || 'saludable'
            };

            // Guardar preferencias en el backend
            await menuService.updatePreferences(backendPreferences);

            // Guardar preferencias en localStorage (en formato backend)
            const userDataStr = localStorage.getItem('user_data');
            if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                userData.preferences = backendPreferences;
                localStorage.setItem('user_data', JSON.stringify(userData));
            }

            // Actualizar estados
            setHasPreferences(true);
            setShowPreferencesWizard(false);

            // Cargar los datos iniciales
            // Forzar loader hasta que haya menús
            setIsLoadingMenus(true);
            setAllMenus([]);
            await loadInitialData();
        } catch (error) {
            setError(t('preferences.errors.save') || t('common.error'));
        }
    };

    // Si no tiene preferencias, mostrar el wizard obligatoriamente
    if (showPreferencesWizard) {
        return (
            <Layout title={`${t('preferences.title')} 🍽️`}>
                <Alert variant="info" className="max-w-2xl mx-auto mb-6">
                    <p className="text-center">
                        👋 <strong>{t('plan.welcome.title') || '¡Bienvenido!'}</strong><br />
                        {t('plan.welcome.body') || 'Antes de comenzar, necesitamos conocer tus preferencias alimentarias para crear menús personalizados para ti.'}
                    </p>
                </Alert>
                <PreferencesWizard
                    onMenuGenerated={handlePreferencesCompleted}
                    setLoading={setIsLoading}
                    loadUserPreferences={false}
                />
            </Layout>
        );
    }

    return (
        <Layout title={`${t('plan.page.title') || t('navigation.plan')} 🍽️`}>
            {/* Mensaje informativo sobre el propósito de esta página */}
            <Alert className="max-w-2xl mx-auto mb-4">
                <p className="text-center text-sm sm:text-base px-2">
                    <strong>{t('plan.info.header') || 'Menús para la próxima semana'}</strong><br />
                    <span className="hidden sm:inline">{t('plan.info.long') || 'Esta es la página donde puedes generar y confirmar los menús que quieres para la semana que viene. Aquí podrás revisar tus opciones y confirmar cuál será tu plan de comidas.'}</span>
                    <span className="sm:hidden">{t('plan.info.short') || 'Genera y confirma tus menús semanales aquí.'}</span>
                </p>
            </Alert>

            {/* Mensaje temporal informativo sobre generación de menús */}
            <Alert variant="info" className="max-w-2xl mx-auto mb-4">
                <p className="text-center text-sm sm:text-base px-2">
                    {t('plan.generatingMenusInfo') || 'Si no ves los menús es que todavía se están generando, en seguida se mostrarán'}
                </p>
            </Alert>

            {/* Mostrar contador de menús para usuarios logueados */}
            {/*menuStatus && (
                <Alert variant={menuStatus.limit_reached ? "warning" : "success"} className="max-w-2xl mx-auto">
                    <p>
                        📊 Menús generados esta semana: {menuStatus.menus_generated} |
                        Restantes: {menuStatus.menus_remaining}
                    </p>
                    {menuStatus.limit_reached && (
                        <p className="font-bold mt-2">
                            ⚠️ Has alcanzado el límite semanal de menús
                        </p>
                    )}
                </Alert>
            )*/}

            {error && (
                <Alert variant="error" className="max-w-2xl mx-auto">
                    <p>{error}</p>
                </Alert>
            )}


            {menu && <MenuDisplay menu={menu} onConfirm={handleMenuConfirmation} canGiveFeedback={true} feedbackService={feedbackService} onToggleRepeatable={handleToggleRepeatable} />}

            {/* Sistema de tabs para las 3 secciones */}
            <div className="max-w-4xl mx-auto px-4">
                {/* Barra de tabs con colores diferentes */}
                <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                    <button
                        onClick={() => setActiveTab('currentWeek')}
                        className={`px-4 py-3 font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${activeTab === 'currentWeek'
                            ? 'border-b-2 border-green-600 text-green-600 bg-green-50'
                            : 'text-gray-600 hover:text-green-700 hover:bg-green-50'
                            }`}
                    >
                        {t('plan.sections.currentWeek') || 'Semana en curso'}
                    </button>
                    <button
                        onClick={() => setActiveTab('nextWeek')}
                        className={`px-4 py-3 font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${activeTab === 'nextWeek'
                            ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                            : 'text-gray-600 hover:text-blue-700 hover:bg-blue-50'
                            }`}
                    >
                        {t('plan.sections.nextWeek') || 'Próxima semana'}
                    </button>
                    <button
                        onClick={() => setActiveTab('previousWeeks')}
                        className={`px-4 py-3 font-medium text-sm sm:text-base transition-colors whitespace-nowrap ${activeTab === 'previousWeeks'
                            ? 'border-b-2 border-gray-600 text-gray-700 bg-gray-50'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                    >
                        {t('plan.sections.previousWeeks') || 'Semanas pasadas'}
                    </button>
                </div>

                {/* Contenido de los tabs */}
                <div className="mt-6">
                    {/* Semana en curso */}
                    {activeTab === 'currentWeek' && (
                        <WeekSection
                            title={t('plan.sections.currentWeek') || 'Semana en curso'}
                            menus={currentWeekMenus}
                            menuMetadata={currentWeekMetadata}
                            onMenuSelect={handleMenuSelect}
                            onToggleRepeatable={handleToggleRepeatable}
                            onMenusReordered={loadInitialData}
                            isLoading={isLoadingMenus}
                            isCurrentWeek={true}
                        />
                    )}

                    {/* Próxima semana */}
                    {activeTab === 'nextWeek' && (
                        <WeekSection
                            title={t('plan.sections.nextWeek') || 'Próxima semana'}
                            menus={nextWeekMenus}
                            menuMetadata={nextWeekMetadata || menuMetadata}
                            onMenuSelect={handleMenuSelect}
                            onToggleRepeatable={handleToggleRepeatable}
                            onMenusReordered={loadInitialData}
                            isLoading={isLoadingMenus}
                        />
                    )}

                    {/* Semanas pasadas */}
                    {activeTab === 'previousWeeks' && (
                        <div className="space-y-8">
                            {Object.keys(previousWeeksMenus).length === 0 ? (
                                <Alert showIcon={false} className="max-w-2xl mx-auto">
                                    <div className="text-center py-12 px-4">
                                        {/*<p className="text-2xl font-bold mb-3 text-gray-800">
                                            {t('plan.noPreviousWeeks.title') || 'Tu historial está más limpio que tu nevera después de hacer la compra'}
                                        </p>*/}
                                        <p className="text-gray-600 text-lg">
                                            {t('plan.noPreviousWeeks.message') || 'Aquí aparecerán los menús que ya hayas cocinado. De momento, esto está más vacío que una despensa un domingo por la noche.'}
                                        </p>
                                    </div>
                                </Alert>
                            ) : (
                                Object.entries(previousWeeksMenus).map(([weekStartDate, weekData]) => {
                                    const weekStart = new Date(weekStartDate);
                                    const weekEnd = new Date(weekStart);
                                    weekEnd.setDate(weekEnd.getDate() + 6);
                                    const weekTitle = `${weekStart.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}`;

                                    return (
                                        <WeekSection
                                            key={weekStartDate}
                                            title={weekTitle}
                                            menus={weekData.menus || []}
                                            menuMetadata={weekData.menu_metadata || null}
                                            onMenuSelect={handleMenuSelect}
                                            onToggleRepeatable={handleToggleRepeatable}
                                            onMenusReordered={loadInitialData}
                                            isLoading={isLoadingMenus}
                                        />
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Fallback para estructura legacy deshabilitado - ahora se usa el sistema de tabs con WeekSection */}

            {/* Botón para confirmar semana y generar lista de compra (solo para próxima semana y solo en el tab nextWeek) */}
            {activeTab === 'nextWeek' && nextWeekMenus.length > 0 && (
                <div className="max-w-2xl mx-auto my-6 sm:my-8 px-4">
                    <Alert variant="info" className="mb-4">
                        <p className="text-center text-sm sm:text-base px-2">
                            📋 <strong>{t('plan.confirm.header') || '¿Listo para confirmar tu semana?'}</strong><br />
                            <span className="hidden sm:inline">{t('plan.confirm.long') || 'Al confirmar, se marcarán todos los menús como revisados y se generará tu lista de compra consolidada para toda la semana.'}</span>
                            <span className="sm:hidden">{t('plan.confirm.short') || 'Se generará tu lista de compra para toda la semana.'}</span>
                        </p>
                    </Alert>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={handleConfirmWeek}
                        disabled={isConfirmingWeek || isWeekConfirmed}
                        className={`w-full font-bold py-4 px-4 sm:px-6 rounded-lg shadow-lg min-h-[56px] text-base sm:text-lg ${isWeekConfirmed
                            ? 'bg-gray-500 text-white cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 active:bg-green-800 text-white'
                            }`}
                    >
                        {isConfirmingWeek ? (
                            <>
                                <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                <span className="hidden sm:inline">{t('plan.page.confirming')}</span><span className="sm:hidden">{t('plan.page.confirming')}</span>
                            </>
                        ) : isWeekConfirmed ? (
                            <>
                                ✅ <span className="hidden sm:inline">{t('plan.confirm.confirmed') || 'Menú confirmado'}</span><span className="sm:hidden">{t('plan.confirm.confirmed') || 'Confirmado'}</span>
                            </>
                        ) : (
                            <>
                                ✅ <span className="hidden sm:inline">{t('plan.page.confirmWeek')}</span><span className="sm:hidden">{t('common.confirm') || t('plan.page.confirmWeek')}</span>
                            </>
                        )}
                    </Button>
                </div>
            )}
        </Layout>
    );
};

export default Plan;
